import React, { useEffect } from "react";
import {
  Box,
  makeStyles,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  TableSortLabel,
} from "@material-ui/core";
import { StyledTableCell, StyledTableRow } from "components/table";
import { SkeletonTableRow } from "components/skeleton/skeleton.component";
import Alert from "@material-ui/lab/Alert";
import { TablePaginationActions } from "components/tablePagination";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import {AccessRights} from "lib/constants/accessRights";
import { getLang } from "app/feature/constants";
import StickyLeftTableCell from "components/table/stickyLeftTableCell";
import { KeyboardArrowDown } from "@material-ui/icons"
import moment from "moment";
import "moment/min/locales.min";
import {canAccess} from "../../../../profile/redux/selector/profile.selector";

const useStyle = makeStyles((theme) => ({
  table: {
    tableLayout: "auto",
    whiteSpace: "nowrap",
    borderCollapse: "separate",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      minWidth: 0,
    },
    "& th,td": {
      fontSize: ".75rem",
      borderRight: "1px solid rgba(58, 77, 84, 0.1)",
      padding: theme.spacing(1, 2, 1, 2),
    },
    "& th": {
      color: theme.palette.text.secondary,
      fontWeight: theme.typography.fontWeightBold,
    },
    "& th:last-child, td:last-child": {
      width: 37,
      borderRight: "0px",
    },
    "& th th": {
      borderBottom: "none !important",
    },
    "& td td": {
      borderBottom: "none !important",
    },
  },
  emptyContainer: {
    textAlign: "center"
  }
}));

export default function OtpTableComponent({
  rows,
  isFetching,
  page,
  rowsPerPage,
  handleChangePage,
  handleChangeRowsPerPage,
  totalRecords,
  search,
  paginationOptions,
  lang,
  tableFilter,
  language,
  changeOrder
}) {
  const classes = useStyle();
  const history = useHistory();

  if (language === "ZH") {
    moment.locale('zh-cn')
  } else if (language === "TL") {
    moment.locale('tl-ph')
  } else {
    moment.locale(language)
  }

  const hasOtpReportViewAccessRight = useSelector(state => canAccess(state, AccessRights.OtpReporting.MODULE));

  useEffect(() => {
    if (!hasOtpReportViewAccessRight) {
      history.push('/admin/dashboard')
    }
  }, [history, hasOtpReportViewAccessRight])

  return (
    <>
      {rows.length > 0 || isFetching ? (
        <TableContainer>
          <Table
            className={classes.table}
            size="small"
            aria-label="otp report table"
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <StyledTableCell>{getLang(lang, "label.NUM")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.CUSTOMER_NAME")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.CONTACT_NUMBER")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.STATUS")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.SENT")}</StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={true}
                    direction={tableFilter.orderBy === 'registered_date' ? tableFilter.order : 'desc'}
                    onClick={() => {
                      const order = tableFilter.order === 'asc' ? "desc" : "asc"
                      changeOrder(order, 'registered_date')
                    }}
                    IconComponent={KeyboardArrowDown}
                  >
                    {getLang(lang, "label.REGISTERED_DATE")}
                  </TableSortLabel>
                </StyledTableCell>
                <StyledTableCell>
                  <TableSortLabel
                    active={true}
                    direction={tableFilter.orderBy === 'verified_date' ? tableFilter.order : 'desc'}
                    onClick={() => {
                      const order = tableFilter.order === 'asc' ? "desc" : "asc"
                      changeOrder(order, 'verified_date')
                    }}
                    IconComponent={KeyboardArrowDown}
                  >
                    {getLang(lang, "label.VERIFIED_DATE")}
                  </TableSortLabel>
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isFetching
                ? [...new Array(5)].map((v, index) => (
                  <SkeletonTableRow key={index} columnCount={7} />
                ))
                : rows.map((row, index) => {
                  return (
                    <StyledTableRow
                      key={index}
                      hover
                      role="checkbox"
                      tabIndex={-1}
                    >
                      <StickyLeftTableCell>
                        <StyledTableCell>
                          {index + 1 + rowsPerPage * page}
                        </StyledTableCell>
                      </StickyLeftTableCell>
                      <StyledTableCell>{row.customer_name}</StyledTableCell>
                      <StyledTableCell>{row.contact_number}</StyledTableCell>
                      <StyledTableCell style={{ color: row.status === 1 ? "#32D583" : "#FDB022" }}>{row.status === 1 ? getLang(lang, "label.VERIFIED") : getLang(lang, "label.PENDING")} {row.status === 1 && row.verification_method === 1 ? "(" + getLang(lang, "label.EMAIL") + ")" : ''}</StyledTableCell>
                      <StyledTableCell>{row.sent}</StyledTableCell>
                      <StyledTableCell>
                        {row.registered_date
                          ? moment.utc(row.registered_date).local().format('lll')
                          : '-'}
                      </StyledTableCell>
                      <StyledTableCell>
                        {row.verified_date
                          ? moment.utc(row.verified_date).local().format('lll')
                          : '-'}
                      </StyledTableCell>
                    </StyledTableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : !search && !isFetching && (
        <Box style={{ overflowX: 'auto', backgroundColor: '#fff' }}>
          <Table
            className={classes.table}
            size="small"
            aria-label="otp report table"
            stickyHeader
          >
            <TableHead>
              <TableRow>
                <StyledTableCell>{getLang(lang, "label.NUM")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.CUSTOMER_NAME")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.CONTACT_NUMBER")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.STATUS")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.SENT")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.REGISTERED_DATE")}</StyledTableCell>
                <StyledTableCell>{getLang(lang, "label.VERIFIED_DATE")}</StyledTableCell>
              </TableRow>
            </TableHead>
          </Table>
          <Box py={9} className={classes.emptyContainer}>
            <Typography variant="body1">
              {getLang(lang, "paragraph.NO_OTP_REPORT")}
            </Typography>
          </Box>
        </Box>

      )
      }
      {
        !isFetching && rows.length === 0 && search && (
          <Alert severity="info">{getLang(lang, "message.info.NO_OTP_REPORT")}</Alert>
        )
      }
      {
        !isFetching && rows.length > 0 && (
          <TablePagination
            style={{
              backgroundColor: "#fff",
              boxShadow: "0px -1px 0px rgba(58, 77, 84, 0.15)",
            }}
            ActionsComponent={TablePaginationActions}
            rowsPerPageOptions={paginationOptions}
            component="div"
            count={totalRecords}
            rowsPerPage={rowsPerPage}
            page={page}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            labelRowsPerPage={getLang(lang, 'label.ROWS_PER_PAGE')}
            labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString("en-US")}-${to.toLocaleString("en-US")} ${getLang(lang, 'label.OF')} ${count.toLocaleString("en-US")}`}
          />
        )
      }
    </>
  );
}